import {
  mdiCallSplit,
  mdiCancel,
  mdiCashRefund,
  mdiCheck,
  mdiPackageVariantClosed,
  mdiProgressClock,
  mdiSourceBranch
} from '@mdi/js'

import { formatSlaDateTime } from '@/modules/company/Approvals/PriorityPayments/slaFormat'
import type {
  AffiliatePackageDetailResponse,
  AffiliatePackageRow,
  AffiliateTimelineEvent
} from '@/Network/Types/Responses/AntecipeAffiliateResponses'

import { PACKAGE_STAGE_LABELS, nextStage } from './labels'

export type TimelineTone = 'done' | 'warn' | 'pending'

export interface TimelineEntry {
  key: string
  icon: string
  title: string
  when: string
  description: string
  linkPackageId: number | null
  tone: TimelineTone
}

const instantOf = (event: AffiliateTimelineEvent): string | null =>
  event.at ?? event.detectedAt ?? null

/** Evento sem instante (Antecipe não informou e o sync não registrou) vai para o fim. */
const sortKey = (event: AffiliateTimelineEvent): number => {
  const iso = instantOf(event)
  const ms = iso ? Date.parse(iso) : Number.NaN
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY
}

const formatWhen = (event: AffiliateTimelineEvent): string => {
  const iso = instantOf(event)
  if (!iso || !Number.isFinite(Date.parse(iso))) return ''
  // Sem `at`, o instante é o da detecção pelo sync: sempre aproximado (pacote legado).
  const approx = event.approx || !event.at
  return `${formatSlaDateTime(iso)}${approx ? ' (aprox.)' : ''}`
}

/** Etapa fora do enum (backend mais novo que o web) cai no rótulo genérico, sem lançar. */
const statusChangedTitle = (stage: AffiliateTimelineEvent['stage']): string => {
  const label: string | undefined = stage ? PACKAGE_STAGE_LABELS[stage] : undefined
  return label ? `Entrou em ${label.toLowerCase()}` : 'Mudou de etapa'
}

function toEntry(
  event: AffiliateTimelineEvent,
  index: number,
  pkg: AffiliatePackageRow
): TimelineEntry {
  const base = {
    key: `${event.type}-${index}`,
    when: formatWhen(event),
    description: '',
    linkPackageId: null as number | null
  }
  switch (event.type) {
    case 'CREATED':
      return {
        ...base,
        icon: mdiPackageVariantClosed,
        tone: 'done',
        title: 'Pedido de antecipação',
        description: pkg.originPackageId
          ? `Pacote criado na divisão do #${pkg.originPackageId}`
          : `${pkg.itemCount} ${pkg.itemCount === 1 ? 'plantão enviado' : 'plantões enviados'} pelo médico`
      }
    case 'STATUS_CHANGED':
      return { ...base, icon: mdiCheck, tone: 'done', title: statusChangedTitle(event.stage) }
    case 'ITEM_MOVED_IN':
      return {
        ...base,
        icon: mdiSourceBranch,
        tone: 'warn',
        title: event.relatedPackageId
          ? `Separado do pacote #${event.relatedPackageId}`
          : 'Plantão recebido de outro pacote',
        description: 'A empresa médica recusou este plantão na conferência do pacote original.',
        linkPackageId: event.relatedPackageId
      }
    case 'ITEM_MOVED_OUT':
      return {
        ...base,
        icon: mdiCallSplit,
        tone: 'warn',
        title: 'Conferência com recusa',
        description: event.relatedPackageId
          ? `Plantão recusado pela empresa foi para o pacote #${event.relatedPackageId}.`
          : 'Plantão recusado pela empresa saiu deste pacote.',
        linkPackageId: event.relatedPackageId
      }
    case 'PAID':
      return { ...base, icon: mdiCheck, tone: 'done', title: 'Pago' }
    case 'CANCELED':
      return { ...base, icon: mdiCancel, tone: 'pending', title: 'Cancelado' }
    case 'REVERSED':
      return {
        ...base,
        icon: mdiCashRefund,
        tone: 'warn',
        title: 'Pagamento desfeito no Antecipe',
        description:
          'O pacote saiu de Pago depois de contado; o ajuste entra no repasse da competência em que foi detectado.'
      }
    default:
      return { ...base, icon: mdiCheck, tone: 'done', title: String(event.type) }
  }
}

export function buildTimeline(detail: AffiliatePackageDetailResponse): TimelineEntry[] {
  const pkg = detail.package
  const entries = detail.timeline
    .map((event, index) => ({ event, index }))
    .sort((a, b) => sortKey(a.event) - sortKey(b.event) || a.index - b.index)
    .map(({ event, index }) => toEntry(event, index, pkg))
  const next = nextStage(pkg.stage)
  if (next) {
    entries.push({
      key: 'next',
      icon: mdiProgressClock,
      title: `Próxima etapa: ${PACKAGE_STAGE_LABELS[next]}`,
      when: '',
      description: '',
      linkPackageId: null,
      tone: 'pending'
    })
  }
  return entries
}
